import { createReducer, createAction } from '@reduxjs/toolkit';

import { backendCall, createApiCall } from '@reducers/utils';
import { getByHash } from '@reducers/imageHandling';
import type { AppThunk } from '@store/types';
import type { ResponseHeaders } from '@reducers/utils/types';
import type {
  ProductionOrdersState,
  ProductionOrder,
  ProductionOrderSearchResponse,
} from './types';

// Re-export types for consumers
export type {
  ProductionOrdersState,
  ProductionOrder,
  ProductionOrderSearchResponse,
} from './types';

// === Initial State ===

const initState: ProductionOrdersState = {
  loaded: false,
  canUpload: true,
  data: {},
  page: 1,
  showLoading: true,
  isError: false,
  requestTimeEndMS: 0,
  pageSize: 100,
  pagination: {},
};

// === Actions ===

export const set = createAction<ProductionOrderSearchResponse>('productionOrders/set');
export const setNew = createAction<ProductionOrderSearchResponse>('productionOrders/setNew');
export const setPagination = createAction<Record<string, unknown>>('productionOrders/setPagination');
export const updatePage = createAction('productionOrders/updatePage');
export const setAllow = createAction<boolean>('productionOrders/setAllow');
export const setRequestTimeEnd = createAction<number>('productionOrders/setRequestTimeEnd');
export const setPage = createAction<number>('productionOrders/setPage');
export const setLoading = createAction<boolean>('productionOrders/setLoading');
export const setError = createAction('productionOrders/setError');
export const startLoading = createAction('productionOrders/startLoading');
export const setPO = createAction<ProductionOrder[]>('productionOrders/setPO');
export const refreshInfo = createAction<Record<string, string>>('productionOrders/refreshInfo');
export const clear = createAction('clear');

// === Thunks ===

export const getData = (cb?: () => void): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const {
    productionOrders: { page, pageSize },
  } = getState();
  dispatch(startLoading());

  const refreshTime = await dispatch(
    backendCall(
      createApiCall((api) => api.productionOrders.searchProductionOrders, {
        params: { pagination: { pageSize, pageNumber: page, includeCount: true } },
        saveDuration: setRequestTimeEnd,
        onSuccess: (data: ProductionOrderSearchResponse, { paginationString }: ResponseHeaders) => {
          if (paginationString) {
            const pagination = JSON.parse(paginationString);
            dispatch(setPagination(pagination));
          }
          dispatch(set(data));
          if (cb) {
            cb();
          }
        },
        onError: (error) => {
          console.log(error, 'error');
          dispatch(setError());
        },
      })
    )
  );
  if (refreshTime) {
    dispatch(refreshInfo((refreshTime as Record<string, Record<string, string>>).refreshInfo));
  }
};

export const loadNewData = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const {
    productionOrders: { page, pageSize },
  } = getState();
  dispatch(updatePage());

  await dispatch(
    backendCall(
      createApiCall((api) => api.productionOrders.searchProductionOrders, {
        params: { pagination: { pageSize, pageNumber: page + 1, includeCount: true } },
        onSuccess: (data: ProductionOrderSearchResponse) => {
          dispatch(setNew(data));
        },
        onError: () => {},
      })
    )
  );
};

let signalRFavoritePool: Record<string, unknown>[] = [];
let eventId: ReturnType<typeof setTimeout> | null = null;
export const updatePOFavoriteSignalR = (e: Record<string, unknown>): AppThunk<Promise<void>> => async (dispatch) => {
  if (eventId) {
    clearTimeout(eventId);
  }
  signalRFavoritePool.push(e);
  eventId = setTimeout(() => {
    dispatch(runSignalRWorker());
  }, 3000);
};

const runSignalRWorker = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const {
    productionOrders: { data },
  } = getState();

  if (!data || !(data as ProductionOrderSearchResponse).searchResult) {
    return;
  }

  const hashes = signalRFavoritePool.map((e) => e.imageHash);
  const po = (data as ProductionOrderSearchResponse).searchResult || [];
  const newPO: ProductionOrder[] = [];

  const fetchedImages: Record<string, unknown> = {};

  for (const index in po) {
    const pOrder = po[index] as Record<string, unknown>;
    let favoritePartImage = pOrder.favoritePartImage;
    if (pOrder.favoritePartImage && (pOrder.favoritePartImage as Record<string, unknown>).imageHash) {
      const compare = async () => {
        const img = pOrder.favoritePartImage as Record<string, unknown>;
        if (hashes.includes(img.imageHash)) {
          const signalImage = signalRFavoritePool.find((i) => i.imageHash === img.imageHash) as Record<string, unknown>;
          const needReplace = (signalImage.tags as Array<{ isFavorite: boolean }>).some((t) => !t.isFavorite);
          if (!needReplace) {
            return;
          }
          const replaceBy = signalRFavoritePool.find((newImg) => {
            if (newImg.imageHash === img.imageHash) {
              return false;
            }

            return (newImg.tags as Array<{ scopeId: unknown; scopeReferenceId: unknown }>).every((newT) => {
              return (signalImage.tags as Array<{ scopeId: unknown; scopeReferenceId: unknown }>).find(
                (oldT) => oldT.scopeId === newT.scopeId && oldT.scopeReferenceId === newT.scopeReferenceId
              );
            });
          });

          if (!replaceBy) {
            return;
          }
          if (fetchedImages[replaceBy.imageHash as string]) {
            favoritePartImage = fetchedImages[replaceBy.imageHash as string];
            return;
          }

          const searchFilters = {
            ImageHash: {
              value: replaceBy.imageHash,
              valueType: 'number',
            },
            Part: {
              value: `${pOrder.partId}`,
              valueType: 'number',
            },
            ProductionOrder: {
              value: `${pOrder.productionOrderId}`,
              valueType: 'number',
            },
          };
          const newImgResult = await dispatch(getByHash(searchFilters));
          const newImg = (newImgResult as Record<string, unknown[]>).documents[0];
          if (!newImg) {
            return;
          }
          fetchedImages[replaceBy.imageHash as string] = newImg;
          favoritePartImage = newImg;
        }
      };
      await compare();
    }
    newPO.push({
      ...pOrder,
      favoritePartImage,
    } as ProductionOrder);
  }
  signalRFavoritePool = [];
  dispatch(setPO(newPO));
};

// === Reducer ===

export default createReducer<ProductionOrdersState>(initState, (builder) => {
  builder
    .addCase(setRequestTimeEnd, (state, action) => ({
      ...state,
      requestTimeEndMS: action.payload,
    }))
    .addCase(set, (state, action) => ({
      ...state,
      loaded: true,
      data: action.payload,
    }))
    .addCase(updatePage, (state) => ({
      ...state,
      page: state.page + 1,
    }))
    .addCase(setNew, (state, action) => ({
      ...state,
      loaded: true,
      data: {
        ...(state.data as ProductionOrderSearchResponse),
        ...action.payload,
        searchResult: [
          ...((state.data as ProductionOrderSearchResponse).searchResult || []),
          ...(action.payload.searchResult || []),
        ],
      },
    }))
    .addCase(setPO, (state, action) => ({
      ...state,
      loaded: true,
      data: {
        ...(state.data as ProductionOrderSearchResponse),
        searchResult: action.payload,
      },
    }))
    .addCase(startLoading, (state) => ({
      ...state,
      isError: false,
      loaded: false,
      data: {},
    }))
    .addCase(setError, (state) => ({
      ...state,
      isError: true,
    }))
    .addCase(setAllow, (state, action) => ({
      ...state,
      canUpload: action.payload,
    }))
    .addCase(setPage, (state, action) => ({
      ...state,
      page: action.payload,
    }))
    .addCase(setLoading, (state, action) => ({
      ...state,
      showLoading: action.payload,
    }))
    .addCase(refreshInfo, (state, action) => ({
      ...state,
      refreshInfo: action.payload,
    }))
    .addCase(setPagination, (state, action) => ({
      ...state,
      pagination: action.payload,
    }))
    .addCase(clear, () => {
      return initState;
    });
});
